/**
 * Filename sanitization utilities for web-safe storage paths
 */

/**
 * Sanitizes a filename to be web-safe for Firebase Storage paths.
 *
 * - Replaces spaces with underscores
 * - Removes special characters (keeps letters, digits, underscore, dash, dot)
 * - Converts to lowercase
 * - Limits total length to 100 characters
 *
 * @param name - Original filename (e.g., "My Photo 2024.jpg")
 * @returns Sanitized filename (e.g., "my_photo_2024.jpg")
 */
export function sanitizeFilename(name: string): string {
  // Get file extension
  const lastDotIndex = name.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0 && lastDotIndex < name.length - 1;
  const extension = hasExtension ? name.slice(lastDotIndex).toLowerCase() : "";
  const baseName = hasExtension ? name.slice(0, lastDotIndex) : name;

  // Sanitize base name:
  // 1. Replace spaces with underscores
  // 2. Remove all characters except letters, digits, underscore, dash
  // 3. Convert to lowercase
  // 4. Remove consecutive underscores/dashes
  const sanitizedBase = baseName
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .toLowerCase()
    .replace(/[_-]+/g, "_")
    .replace(/^[_-]+|[_-]+$/g, ""); // Trim leading/trailing underscores/dashes

  // Calculate max base length (100 - extension length)
  const maxBaseLength = 100 - extension.length;
  const truncatedBase = sanitizedBase.slice(0, maxBaseLength) || "file";

  return `${truncatedBase}${extension}`;
}
